import { format } from "date-fns";
import ScheduleType from "../pages/Schedules/ScheduleType";

export const SCHEDULE_STATUS_SCHEDULED = ScheduleType.SCHEDULED;
export const SCHEDULE_SEARCH_DEFAULT_PAGE = 1;
export const SCHEDULE_SEARCH_DEFAULT_PER_PAGE = 15;
export const SCHEDULE_SEARCH_MAX_PER_PAGE = 100;

export type ScheduleSearchParamsInput = {
  search?: string;
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: number;
  page?: number;
  perPage?: number;
  teamId?: number;
  upcomingOnly?: boolean;
};

export function formatLocalIsoDate(date: Date = new Date()): string {
  return format(date, "yyyy-MM-dd");
}

export function isoDateFromDateTime(raw?: string | null): string | undefined {
  const match = String(raw ?? "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1];
}

export function isDateRangeValid(dateFrom?: string, dateTo?: string): boolean {
  if (!dateFrom || !dateTo) return true;
  return dateFrom <= dateTo;
}

export function canCreateCardFromSchedule(item: {
  can_create_card?: boolean;
  has_card?: boolean;
  status?: number;
}): boolean {
  if (typeof item.can_create_card === "boolean") {
    return item.can_create_card;
  }

  return item.status === SCHEDULE_STATUS_SCHEDULED && !item.has_card;
}

function clampPerPage(perPage?: number): number {
  const value = perPage ?? SCHEDULE_SEARCH_DEFAULT_PER_PAGE;
  if (!Number.isFinite(value) || value < 1) return SCHEDULE_SEARCH_DEFAULT_PER_PAGE;
  return Math.min(Math.floor(value), SCHEDULE_SEARCH_MAX_PER_PAGE);
}

export function buildKanbanScheduleSearchInput(input: {
  mode: "client" | "date";
  search: string;
  date: string;
  dateTo: string;
  page: number;
}): ScheduleSearchParamsInput {
  const isDateRange = input.mode === "date" && Boolean(input.date && input.dateTo);

  return {
    status: SCHEDULE_STATUS_SCHEDULED,
    page: input.page,
    search: input.mode === "client" ? input.search : undefined,
    date: input.mode === "date" && input.date && !input.dateTo ? input.date : undefined,
    dateFrom: isDateRange ? input.date : undefined,
    dateTo: isDateRange ? input.dateTo : undefined,
  };
}

export function buildScheduleSearchParams(
  input: ScheduleSearchParamsInput,
  today = formatLocalIsoDate()
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: input.page && input.page > 0 ? Math.floor(input.page) : SCHEDULE_SEARCH_DEFAULT_PAGE,
    per_page: clampPerPage(input.perPage),
  };

  const search = input.search?.trim();
  if (search) {
    params.search = search;
  }

  if (input.date) {
    params.date = input.date;
  } else if (input.dateFrom || input.dateTo) {
    if (input.dateFrom) params.date_from = input.dateFrom;
    if (input.dateTo) params.date_to = input.dateTo;
  } else if (input.upcomingOnly) {
    params.date_from = today;
  }

  if (input.status != null) {
    params.status = input.status;
  }

  if (input.teamId != null) {
    params.team_id = input.teamId;
  }

  return params;
}
